/**
 * [T149-PR5-FE] Insights Skin Payload Contracts — temporary stub.
 *
 * Mirrors backend resolver return shapes for the 11 Insights-page skins ported
 * into the Custom Dashboard. Some shapes depend on resolvers that ship in
 * `feat/t149-pr5-be-insights-resolvers` (BE PR5-BE) and are NOT yet on `dev`.
 * Every speculative field is marked `// TODO[T149-PR5-BE]` so the FE can
 * be retyped against the locked envelopes after the BE PR merges with no
 * downstream renames.
 *
 * Stable resolvers (already on `dev`):
 *   - `categoryBreakdownFacet`  → tx-by-{type,category,account} + tag-spend variants
 *   - `spendingTrend`           → trends-configurable
 *   - `tagSpend`                → tag-spending-area
 *   - `dayOfWeekSpending`       → day-of-week-bar
 *   - `topMerchantsList`        → top-merchants-bar
 *   - `dailyCashflow`           → net-cashflow-line (uses `points[].net`)
 *
 * Pending resolvers (BE PR5-BE):
 *   - `insightsHeatmap`            → spending-heatmap-calendar
 *   - `insightsRecurringVsOneOff`  → recurring-vs-discretionary
 *   - `insightsAmountHistogram`    → amount-histogram-bar
 *
 * @module lib/insightsContracts
 * @see docs/design/T149-WIDGET-PARITY-SPEC.md (Joint UX↔FE Brainstorm — Insights Port — 2026-04-24)
 */

// [T247-PY] Workbench payload type — imported from resolverContracts so the
// insights surface can reference the workbench without a circular import.
import type { TransactionInsightWorkbenchPayload } from './resolverContracts';


// ── 1. tx-by-type-donut · 2. tx-by-category-bar · 3. tx-by-account-bar ───────

/**
 * Single facet slice — re-used by every variant of `categoryBreakdownFacet`.
 * Field name `name` is the human-readable label (category / account / type).
 */
export interface FacetSlice {
  /** Stable id (categoryId / accountId / "income" | "expense" | "transfer"). */
  id: string;
  name: string;
  amount: number;
  count: number;
  /** 0-100 share of the window total. */
  percentage: number;
  /** Optional emoji from category / account; absent for type. */
  emoji?: string;
}

export interface FacetBreakdownPayload {
  /** Sorted desc by `amount`. Adapter MAY re-sort for horizontal-bar layouts. */
  slices: FacetSlice[];
  total: number;
  /**
   * Discriminator filled by adapters — the resolver does not echo this back.
   * Skin uses it only when rendering the empty state copy.
   */
  groupBy?: 'type' | 'category' | 'account' | 'tag';
}

// ── 4. recurring-vs-discretionary (BE PR5-BE) ────────────────────────────────

export interface RecurringVsDiscretionaryBucket {
  total: number;
  count: number;
  /** 0-100 — share of `union.total`. */
  percentage: number;
}

/**
 * TODO[T149-PR5-BE]: shape mirrors `insightsRecurringVsOneOff` resolver.
 * UX wording: "discretionary" maps to BE field `oneOff` (alias only — no rename).
 */
export interface RecurringVsDiscretionaryPayload {
  recurring: RecurringVsDiscretionaryBucket;
  /** BE field name; UX label is "Discretionary". */
  oneOff: RecurringVsDiscretionaryBucket;
  totals: { total: number; count: number };
  window: { startDate: string; endDate: string };
}

// ── 5. trends-configurable ───────────────────────────────────────────────────

/** Mirror of `computeSpendingTrend` return — monthly income / expenses / net. */
export interface SpendingTrendPoint {
  /** "YYYY-MM". */
  month: string;
  income: number;
  expenses: number;
  net: number;
}

export interface SpendingTrendPayload {
  months: SpendingTrendPoint[];
}

// ── 6. tag-spending-area ─────────────────────────────────────────────────────

export interface TagSpendEntry {
  tag: string;
  amount: number;
  count: number;
  /** 0-100. */
  percentage: number;
}

export interface TagSpendPayload {
  tags: TagSpendEntry[];
  total: number;
  window: { startDate: string; endDate: string };
}

// ── 7. spending-heatmap-calendar (BE PR5-BE) ─────────────────────────────────

/**
 * TODO[T149-PR5-BE]: shape mirrors `insightsHeatmap` resolver.
 * `intensity` is computed client-side as `total / max(totals)` if BE does
 * not expose it directly. Field name `days` (not `series`) is the locked
 * choice per Round 3 BE-data-contract gap table.
 */
export interface HeatmapDay {
  /** ISO `YYYY-MM-DD`. */
  date: string;
  total: number;
  count: number;
  /** Optional sample tooltip rows. */
  topDescriptions?: string[];
}

export interface HeatmapPayload {
  days: HeatmapDay[];
  window: { startDate: string; endDate: string };
  totals: { sum: number; count: number };
}

// ── 8. day-of-week-bar ───────────────────────────────────────────────────────

// [T263-PY] Per-day category breakdown — mirrors the BE
// `Transaction.aggregate` pipeline extended in `computeDayOfWeekSpending`.
export interface DayOfWeekCategory {
  categoryId: string;
  name: string;
  emoji: string;
  total: number;
  count: number;
}

export interface DayOfWeekBucket {
  /** 1 (Sunday) … 7 (Saturday) — Mongo `$dayOfWeek` convention. */
  dayOfWeek: number;
  /** "Sunday" | "Monday" | … */
  dayName: string;
  totalSpent: number;
  averageSpent: number;
  count: number;
  /**
   * Per-category breakdown for this day, sorted desc by `total`.
   * Empty array when no category data exists (e.g. all txns orphaned).
   */
  categories: DayOfWeekCategory[];
}

/**
 * [T263-PY] Highest-spend day across the window (Mon..Sun).
 * `null` when nothing was spent in the window.
 */
export interface DayOfWeekHighestDay {
  dayOfWeek: number;
  dayName: string;
  totalSpent: number;
}

/**
 * [T263-PY] Highest-spend category across the window.
 * `null` when no category data exists.
 */
export interface DayOfWeekHighestCategory {
  categoryId: string;
  name: string;
  emoji: string;
  total: number;
}

export interface DayOfWeekPayload {
  days: DayOfWeekBucket[];
  /** [T263-PY] Overall mean `totalSpent` across all 7 days. */
  average: number;
  /** [T263-PY] Mean `totalSpent` across Mon..Fri. */
  weekdayAverage: number;
  /** [T263-PY] Mean `totalSpent` across Sat..Sun. */
  weekendAverage: number;
  /** [T263-PY] Day with highest `totalSpent` (null when no data). */
  highestDay: DayOfWeekHighestDay | null;
  /** [T263-PY] Category with highest `total` across all days (null when no data). */
  highestCategory: DayOfWeekHighestCategory | null;
}

// ── 9. top-merchants-bar ─────────────────────────────────────────────────────

export interface TopMerchantEntry {
  name: string;
  totalSpent: number;
  count: number;
  /** 0-100. */
  percentage: number;
}

export interface TopMerchantsPayload {
  merchants: TopMerchantEntry[];
  total: number;
}

// ── top-items-bar ────────────────────────────────────────────────────────────

export interface TopItemEntry {
  name: string;
  count: number;
  totalSpent: number;
}

export interface TopItemsPayload {
  items: TopItemEntry[];
}

// ── 10. amount-histogram-bar (BE PR5-BE) ─────────────────────────────────────

/**
 * TODO[T149-PR5-BE]: shape mirrors `insightsAmountHistogram` resolver.
 * Always returns 5 stable buckets so axis labels never reflow.
 */
export interface AmountHistogramBucket {
  /** Display label, e.g. "0–10k", "500k+". */
  label: string;
  min: number;
  /** `null` for the open-ended top bucket. */
  max: number | null;
  count: number;
  total: number;
}

export interface AmountHistogramPayload {
  buckets: AmountHistogramBucket[];
  totalCount: number;
  window: { startDate: string; endDate: string };
}

// ── 11. net-cashflow-line ────────────────────────────────────────────────────

/**
 * Reuses `dailyCashflow.points[]` from `lib/resolverContracts.ts`.
 * Skin reads `point.net` only — re-declared here so insights skins do not
 * cross-import from the home-mirror contract module.
 */
export interface NetCashflowPoint {
  date: string;
  income: number;
  expense: number;
  net: number;
}

export interface NetCashflowPayload {
  points: NetCashflowPoint[];
}

// ── Defensive normalisers ────────────────────────────────────────────────────

/**
 * Accepts the BE shape `{ categories: [...], total }` (existing) OR
 * `{ slices: [...], total }` (canonical) and produces the canonical envelope.
 * Mirrors the PR3 `normaliseCategoryBreakdownPayload` defensive pattern.
 */
export function normaliseFacetBreakdown(
  raw: unknown,
  groupBy?: FacetBreakdownPayload['groupBy'],
): FacetBreakdownPayload | null {
  if (raw == null || typeof raw !== 'object') return null;
  const r = raw as Record<string, unknown>;
  const arr = (Array.isArray(r.slices) ? r.slices : Array.isArray(r.categories) ? r.categories : null) as
    | Array<Record<string, unknown>>
    | null;
  if (!arr) return null;
  const slices: FacetSlice[] = arr
    .filter((s) => s != null && typeof s === 'object' && typeof s.amount === 'number')
    .map((s) => ({
      // [T149-Phase15-BUG1] Add accountId fallback so tx-by-account-bar slices never get empty string keys.
      id: typeof s.id === 'string'
        ? s.id
        : typeof s.categoryId === 'string'
          ? (s.categoryId as string)
          : typeof s.accountId === 'string'
            ? (s.accountId as string)
            : '',
      name: typeof s.name === 'string' ? s.name : 'Unknown',
      amount: s.amount as number,
      count: typeof s.count === 'number' ? s.count : 0,
      percentage: typeof s.percentage === 'number' ? s.percentage : 0,
      emoji: typeof s.emoji === 'string' ? s.emoji : undefined,
    }));
  return {
    slices,
    total:
      typeof r.total === 'number'
        ? r.total
        : slices.reduce((sum, s) => sum + s.amount, 0),
    groupBy,
  };
}

/**
 * Computes intensity = `total / max(totals)` for each day; tolerant of empty
 * windows. Used by the heatmap skin until BE exposes `intensity` directly.
 */
export function computeHeatmapIntensities(
  days: HeatmapDay[],
): Array<HeatmapDay & { intensity: number }> {
  if (days.length === 0) return [];
  const max = days.reduce((m, d) => (d.total > m ? d.total : m), 0);
  if (max <= 0) return days.map((d) => ({ ...d, intensity: 0 }));
  return days.map((d) => ({ ...d, intensity: Math.max(0, Math.min(1, d.total / max)) }));
}

// ── 12. net-worth-trend-line ─────────────────────────────────────────────────

/**
 * Monthly net-worth history snapshot — for the `net-worth-trend-line` skin.
 * Resolver: `netWorthHistory` (or equivalent snapshot aggregation).
 */
export interface NetWorthTrendPoint {
  /** "YYYY-MM". */
  month: string;
  netWorth: number;
  assets?: number;
  liabilities?: number;
}

export interface NetWorthTrendPayload {
  months: NetWorthTrendPoint[];
}

// ── 13. spending-trend-line ───────────────────────────────────────────────────

/**
 * Simplified single-series expenses line — for the `spending-trend-line` skin.
 * Resolver: `spendingTrend`. Reuses `SpendingTrendPayload` but renders only the
 * `expenses` series. Distinct from `trends-configurable` which renders all three
 * series (income / expenses / net) in a configurable chart.
 */
export type SpendingTrendLinePayload = SpendingTrendPayload;

// ── 14. largest-transactions-list ────────────────────────────────────────────

export interface LargestTransactionItem {
  id: string;
  description: string;
  amount: number;
  date: string;
  type: 'income' | 'expense' | 'transfer';
  categoryName?: string;
  accountName?: string;
  currency?: string;
}

export interface LargestTransactionsPayload {
  items: LargestTransactionItem[];
}

// ── 15. monthly-comparison-bar ───────────────────────────────────────────────

export interface MonthlyComparisonPoint {
  /** "Jan", "Feb", … — short month label. */
  month: string;
  currentYear: number;
  priorYear: number;
}

export interface MonthlyComparisonPayload {
  months: MonthlyComparisonPoint[];
  /** Human-readable comparison label, e.g. "2026 vs 2025". */
  label?: string;
  /**
   * [T149-AUDIT-UX] Semantic label for the current-period bar series.
   * Defaults to "Current period" when absent.
   */
  periodALabel?: string;
  /**
   * [T149-AUDIT-UX] Semantic label for the prior-period bar series.
   * Defaults to "Prior period" when absent.
   */
  periodBLabel?: string;
}

// ── Financial Health Radar (T149-FHR) ────────────────────────────────────────

/**
 * Single scored dimension returned by `financialHealthRadar` resolver.
 * Mirror of `FinancialHealthDimension` in
 * `backend/src/services/resolvers/financialHealthRadar.ts`.
 */
export interface FinancialHealthDimension {
  /** Human-readable dimension name (e.g. "Savings Rate"). */
  dimension: string;
  /** Achieved score (0..max). */
  score: number;
  /** Maximum possible score for this dimension (typically 10). */
  max: number;
}

/** Full payload returned by `computeFinancialHealthRadar`. */
export interface FinancialHealthRadarPayload {
  dimensions: FinancialHealthDimension[];
  /** Weighted average across all dimensions (0..10). */
  overallScore: number;
}

// ── 16. spending-velocity-line [T-WIDGET-REDESIGN-V1 / Wave 2 / T38 retry] ────

/**
 * Single day in the cumulative-daily-spend series. Mirrors the
 * `SpendingVelocityDailyPoint` interface from
 * `backend/src/services/resolvers/spendingVelocity.ts` (the
 * `spendingVelocity` resolver's return type).
 */
export interface SpendingVelocityDailyPoint {
  /** ISO `YYYY-MM-DD` date label. */
  date: string;
  /** Sum of expense transactions on this day. */
  daily: number;
  /** Running total from day 1 of the month to this day (inclusive). */
  cumulative: number;
}

/**
 * Single day in the pace line. Mirrors `SpendingVelocityPacePoint`
 * from `backend/src/services/resolvers/spendingVelocity.ts`.
 */
export interface SpendingVelocityPacePoint {
  /** ISO `YYYY-MM-DD` date label. */
  date: string;
  /** Mean cumulative across the prior months on this day-of-month. */
  average: number;
  /** Standard-deviation band around the average (±1σ). */
  band: { low: number; high: number };
}

/**
 * Payload returned by the `spendingVelocity` resolver — the source
 * of truth is `backend/src/services/resolvers/spendingVelocity.ts`.
 * The home-mirror contract in `lib/resolverContracts.ts` does not
 * currently declare a `SpendingVelocityPayload` interface (it lives
 * only in this insights module), so we define the type locally
 * rather than depending on a (currently missing) cross-module alias.
 * Keeping it inline avoids the broken `import('./resolverContracts')`
 * conditional the previous retry left behind.
 */
export interface SpendingVelocityLinePayload {
  /** Cumulative daily spend for the current month (length = today's day-of-month). */
  currentCumulative: SpendingVelocityDailyPoint[];
  /** Pace line across the entire current month (length = days in current month). */
  paceCumulative: SpendingVelocityPacePoint[];
  /** True if today's cumulative exceeds the pace average for today. */
  isOverPace: boolean;
  /** Positive when over-pace, negative when under-pace; 0 when exactly on pace. */
  overPaceAmount: number;
  /** Today's cumulative spend (= total spent so far this month). */
  currentMonthTotal: number;
  /** Mean total monthly spend across the prior N months. */
  paceMonthAverage: number;
  /** Date window the result covers. */
  period: { startDate: string; endDate: string };
}

// ── 17. net-worth-forecast-line [T36-retry] ─────────────────────────────────

/**
 * Forward-projected net worth driven by the Wave 2 `calendarForecast`
 * resolver. The skin consumes both sub-payloads:
 *
 *   - `forecast` — the `CalendarForecastPayload` from
 *     `backend/src/services/resolvers/calendarForecast.ts`. `points[]`
 *     is the authoritative 90-day forward projection; the skin does NOT
 *     synthesise locally (this closes the loop T28 documented in its
 *     file header's Forecast data note).
 *   - `history`  — the `NetWorthTrendPayload` from the `netWorthHistory`
 *     resolver. Used to draw the stacked-area history above the forecast
 *     strip. Optional so the skin degrades gracefully when the BE has
 *     not yet co-resolved both resolvers.
 */
export type NetWorthForecastLinePayload = {
  forecast: {
    points: Array<{
      date: string;
      projectedBalance: number;
      scheduledIncome?: number;
      scheduledExpenses?: number;
    }>;
    totalChange?: number;
    minBalance?: number;
    maxBalance?: number;
    currentBalance?: number;
    window?: { startDate: string; endDate: string };
  } | null;
  history: NetWorthTrendPayload | null;
};

// ── Insights kind union (extends WidgetKind) ─────────────────────────────────

/**
 * Canonical insights skin ids — extends the base `WidgetKind` union from
 * `resolverContracts.ts`. Listed here so this module is self-sufficient
 * for insights typing.
 */
export type InsightsWidgetKind =
  | 'tx-by-type-donut'
  | 'tx-by-category-bar'
  | 'tx-by-account-bar'
  | 'recurring-vs-discretionary'
  | 'trends-configurable'
  | 'tag-spending-area'
  | 'spending-heatmap-calendar'
  | 'day-of-week-bar'
  | 'top-merchants-bar'
  | 'amount-histogram-bar'
  | 'net-cashflow-line'
  // [T149-AUDIT] 4 additional kinds for resolvers that previously had no skin
  | 'net-worth-trend-line'
  | 'spending-trend-line'
  | 'largest-transactions-list'
  | 'monthly-comparison-bar'
  // [T149-FHR] Financial health radar
  | 'financial-health-radar'
  // [T149-AUDIT Phase 8 Stream B] top-items-bar
  | 'top-items-bar'
  // [T-WIDGET-REDESIGN-V1 / Wave 2 / T38 retry]
  | 'spending-velocity-line'
  // [T36-retry] Net worth forecast line — uses Wave 2 `calendarForecast` resolver
  | 'net-worth-forecast-line'
  // [T41 retry] Goal thermometer — vertical progress fill + days-remaining
  // countdown. Reuses the `goalProgress` resolver, surface the featured goal
  // (first item, sorted by `targetDate` asc, then `current` desc).
  | 'goal-thermometer'
  // [T40 retry] top-categories-waffle — 10x10 waffle grid (1% per cell)
  | 'top-categories-waffle';

export const INSIGHTS_WIDGET_KINDS: readonly InsightsWidgetKind[] = [
  'tx-by-type-donut',
  'tx-by-category-bar',
  'tx-by-account-bar',
  'recurring-vs-discretionary',
  'trends-configurable',
  'tag-spending-area',
  'spending-heatmap-calendar',
  'day-of-week-bar',
  'top-merchants-bar',
  'amount-histogram-bar',
  'net-cashflow-line',
  // [T149-AUDIT] 4 new kinds
  'net-worth-trend-line',
  'spending-trend-line',
  'largest-transactions-list',
  'monthly-comparison-bar',
  // [T149-FHR]
  'financial-health-radar',
  // [T149-AUDIT Phase 8 Stream B]
  'top-items-bar',
  // [T-WIDGET-REDESIGN-V1 / Wave 2 / T38 retry]
  'spending-velocity-line',
  // [T36-retry] Net worth forecast line
  'net-worth-forecast-line',
  // [T41 retry] Goal thermometer
  'goal-thermometer',
  // [T40 retry] top-categories-waffle
  'top-categories-waffle',
] as const;

/** Maps each insights kind to its (typed) resolver payload. */
export interface InsightsResolverPayloadMap {
  'tx-by-type-donut': FacetBreakdownPayload;
  'tx-by-category-bar': FacetBreakdownPayload;
  'tx-by-account-bar': FacetBreakdownPayload;
  'recurring-vs-discretionary': RecurringVsDiscretionaryPayload;
  'trends-configurable': SpendingTrendPayload;
  'tag-spending-area': TagSpendPayload;
  'spending-heatmap-calendar': HeatmapPayload;
  'day-of-week-bar': DayOfWeekPayload;
  'top-merchants-bar': TopMerchantsPayload;
  'amount-histogram-bar': AmountHistogramPayload;
  'net-cashflow-line': NetCashflowPayload;
  // [T149-AUDIT] 4 new kinds
  'net-worth-trend-line': NetWorthTrendPayload;
  'spending-trend-line': SpendingTrendLinePayload;
  'largest-transactions-list': LargestTransactionsPayload;
  'monthly-comparison-bar': MonthlyComparisonPayload;
  // [T149-FHR]
  'financial-health-radar': FinancialHealthRadarPayload;
  // [T149-AUDIT Phase 8 Stream B]
  'top-items-bar': TopItemsPayload;
  // [T-WIDGET-REDESIGN-V1 / Wave 2 / T38 retry]
  'spending-velocity-line': SpendingVelocityLinePayload;
  // [T36-retry] Net worth forecast line — typed payload that combines
  // `calendarForecast` (forecast sub-object) + `netWorthHistory` (history sub-object).
  'net-worth-forecast-line': NetWorthForecastLinePayload;
  // [T41 retry] Goal thermometer — reuses `goalProgress` resolver's `goals[]`
  // array. The skin picks the featured goal (priority: `targetDate` ascending,
  // then `current/target` ratio descending) and renders it as a vertical
  // thermometer with a days-remaining countdown.
  'goal-thermometer': import('./resolverContracts').GoalProgressPayload;
  // [T40 retry] top-categories-waffle — reuses FacetBreakdownPayload (category bar / donut shape)
  'top-categories-waffle': FacetBreakdownPayload;
  // [T247-PY] Workbench — composed time-series with dimension/measure/granularity
  // controls. Payload mirrors backend `TransactionInsightWorkbenchPayload`.
  'transaction-insight-workbench': TransactionInsightWorkbenchPayload;
}


export type InsightsPayloadFor<K extends InsightsWidgetKind> =
  InsightsResolverPayloadMap[K];
